import React, { useState, useEffect, useCallback } from 'react';
import { 
  CheckCircle, 
  XCircle, 
  Clock, 
  Search, 
  AlertTriangle, 
  Receipt, 
  RefreshCw, 
  Eye, 
  ShieldCheck, 
  User, 
  CreditCard,
  Building,
  Calendar
} from 'lucide-react';
import { API_BASE_URL } from '../../config.js';

export default function PaymentVerifications({ orgId, token, showNotification }) {
  const [verifications, setVerifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('PENDING_VERIFICATION'); // ALL, PENDING_VERIFICATION, APPROVED, REJECTED
  const [searchQuery, setSearchQuery] = useState('');
  const [counts, setCounts] = useState({ pending: 0, approved: 0, rejected: 0, approvedToday: 0 });

  // Modals
  const [selectedVerification, setSelectedVerification] = useState(null);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('Payment reference / UTR not found in bank statement');
  const [processing, setProcessing] = useState(false);

  const getHeaders = useCallback(() => {
    const h = { 'Content-Type': 'application/json' };
    const authToken = token || localStorage.getItem('token') || localStorage.getItem('authToken') || '';
    if (authToken) {
      h['Authorization'] = `Bearer ${authToken}`;
    }
    return h;
  }, [token]);

  const fetchVerifications = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    try {
      const url = `${API_BASE_URL}/api/fees/admin/verifications/${orgId}${activeFilter !== 'ALL' ? `?status=${activeFilter}` : ''}`;
      const res = await fetch(url, {
        headers: getHeaders()
      });
      const data = await res.json();
      if (data.success) {
        setVerifications(data.verifications || []);
        setCounts({
          pending: data.pendingCount || 0,
          approved: data.approvedCount || 0,
          rejected: data.rejectedCount || 0,
          approvedToday: data.approvedTodayCount || 0
        });
      } else {
        showNotification?.('error', 'Error', data.error || 'Failed to fetch verifications');
      }
    } catch (err) {
      console.error('Error fetching verifications:', err);
      showNotification?.('error', 'Network Error', err.message);
    } finally {
      setLoading(false);
    }
  }, [orgId, token, activeFilter, showNotification]);

  useEffect(() => {
    fetchVerifications();
  }, [fetchVerifications]);

  const handleApprove = async () => {
    if (!selectedVerification) return;
    setProcessing(true);
    try {
      const url = `${API_BASE_URL}/api/fees/admin/verifications/${selectedVerification.id}/approve`;
      const res = await fetch(url, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ verifiedBy: 'Administrator' })
      });
      const data = await res.json();
      if (data.success) {
        showNotification?.('success', 'Payment Approved', `Payment of ₹${selectedVerification.amount} approved. Receipt ${data.receiptNumber} generated.`);
        setShowApproveModal(false);
        setSelectedVerification(null);
        fetchVerifications();
      } else {
        showNotification?.('error', 'Approval Failed', data.error || 'Failed to approve payment');
      }
    } catch (err) {
      showNotification?.('error', 'Error', err.message);
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!selectedVerification) return;
    setProcessing(true);
    try {
      const url = `${API_BASE_URL}/api/fees/admin/verifications/${selectedVerification.id}/reject`;
      const res = await fetch(url, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
          rejectionReason: rejectionReason,
          verifiedBy: 'Administrator'
        })
      });
      const data = await res.json();
      if (data.success) {
        showNotification?.('success', 'Payment Rejected', 'Payment verification was rejected.');
        setShowRejectModal(false);
        setSelectedVerification(null);
        fetchVerifications();
      } else {
        showNotification?.('error', 'Rejection Failed', data.error || 'Failed to reject payment');
      }
    } catch (err) {
      showNotification?.('error', 'Error', err.message);
    } finally {
      setProcessing(false);
    }
  };

  const filteredVerifications = verifications.filter(v => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      (v.studentName && v.studentName.toLowerCase().includes(q)) ||
      (v.studentId && v.studentId.toLowerCase().includes(q)) ||
      (v.utrNumber && v.utrNumber.toLowerCase().includes(q)) ||
      (v.feeType && v.feeType.toLowerCase().includes(q)) ||
      (v.receiptNo && v.receiptNo.toLowerCase().includes(q))
    );
  });

  return (
    <div style={{ padding: '24px', background: '#f8fafc', minHeight: '100%' }}>
      {/* Header & Stats Cards */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' }}>
        <div>
          <h2 style={{ fontSize: '22px', fontWeight: '800', color: '#0f172a', margin: 0, display: 'flex', alignItems: 'center', gap: '10px' }}>
            <ShieldCheck size={26} color="#2563eb" />
            Payment Verifications (UTR Approvals)
          </h2>
          <p style={{ fontSize: '13px', color: '#64748b', margin: '4px 0 0 0' }}>
            Verify direct UPI transfers against school bank statements before generating receipts & clearing student fee dues.
          </p>
        </div>
        <button
          onClick={fetchVerifications}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px 16px',
            background: '#fff',
            border: '1px solid #cbd5e1',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '13px',
            fontWeight: '600',
            color: '#334155'
          }}
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {/* Summary KPI Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' }}>
        <div 
          onClick={() => setActiveFilter('PENDING_VERIFICATION')}
          style={{ 
            background: '#fff', 
            borderRadius: '12px', 
            padding: '16px 20px', 
            border: activeFilter === 'PENDING_VERIFICATION' ? '2px solid #f59e0b' : '1px solid #e2e8f0', 
            cursor: 'pointer',
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: '13px', fontWeight: '600', color: '#b45309' }}>Pending Verification</span>
            <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: '#fef3c7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Clock size={18} color="#d97706" />
            </div>
          </div>
          <div style={{ fontSize: '28px', fontWeight: '800', color: '#0f172a', marginTop: '8px' }}>{counts.pending}</div>
          <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '2px' }}>Awaiting admin review</div>
        </div>

        <div 
          onClick={() => setActiveFilter('APPROVED')}
          style={{ 
            background: '#fff', 
            borderRadius: '12px', 
            padding: '16px 20px', 
            border: activeFilter === 'APPROVED' ? '2px solid #10b981' : '1px solid #e2e8f0', 
            cursor: 'pointer',
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: '13px', fontWeight: '600', color: '#047857' }}>Approved & Verified</span>
            <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: '#d1fae5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CheckCircle size={18} color="#059669" />
            </div>
          </div>
          <div style={{ fontSize: '28px', fontWeight: '800', color: '#0f172a', marginTop: '8px' }}>{counts.approved}</div>
          <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '2px' }}>Receipts generated</div>
        </div>

        <div 
          onClick={() => setActiveFilter('REJECTED')}
          style={{ 
            background: '#fff', 
            borderRadius: '12px', 
            padding: '16px 20px', 
            border: activeFilter === 'REJECTED' ? '2px solid #ef4444' : '1px solid #e2e8f0', 
            cursor: 'pointer',
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: '13px', fontWeight: '600', color: '#b91c1c' }}>Rejected</span>
            <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: '#fee2e2', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <XCircle size={18} color="#dc2626" />
            </div>
          </div>
          <div style={{ fontSize: '28px', fontWeight: '800', color: '#0f172a', marginTop: '8px' }}>{counts.rejected}</div>
          <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '2px' }}>UTR not found / invalid</div>
        </div>

        <div 
          onClick={() => setActiveFilter('ALL')}
          style={{ 
            background: '#fff', 
            borderRadius: '12px', 
            padding: '16px 20px', 
            border: activeFilter === 'ALL' ? '2px solid #2563eb' : '1px solid #e2e8f0', 
            cursor: 'pointer',
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: '13px', fontWeight: '600', color: '#1e40af' }}>Total Submissions</span>
            <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: '#dbeafe', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Receipt size={18} color="#2563eb" />
            </div>
          </div>
          <div style={{ fontSize: '28px', fontWeight: '800', color: '#0f172a', marginTop: '8px' }}>{counts.pending + counts.approved + counts.rejected}</div>
          <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '2px' }}>All transactions</div>
        </div>
      </div>

      {/* Filter Tabs & Search Bar */}
      <div style={{ background: '#fff', borderRadius: '12px', padding: '16px 20px', border: '1px solid #e2e8f0', marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
        <div style={{ display: 'flex', gap: '8px' }}>
          {[
            { key: 'PENDING_VERIFICATION', label: 'Pending', count: counts.pending },
            { key: 'APPROVED', label: 'Approved', count: counts.approved },
            { key: 'REJECTED', label: 'Rejected', count: counts.rejected },
            { key: 'ALL', label: 'All Submissions', count: counts.pending + counts.approved + counts.rejected }
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveFilter(tab.key)}
              style={{
                padding: '6px 14px',
                borderRadius: '8px',
                fontSize: '13px',
                fontWeight: activeFilter === tab.key ? '700' : '500',
                background: activeFilter === tab.key ? '#002366' : '#f1f5f9',
                color: activeFilter === tab.key ? '#fff' : '#475569',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              {tab.label}
              <span style={{
                background: activeFilter === tab.key ? 'rgba(255,255,255,0.2)' : '#e2e8f0',
                padding: '2px 6px',
                borderRadius: '10px',
                fontSize: '11px'
              }}>
                {tab.count}
              </span>
            </button>
          ))}
        </div>

        <div style={{ position: 'relative', minWidth: '260px' }}>
          <Search size={16} color="#94a3b8" style={{ position: 'absolute', left: '12px', top: '10px' }} />
          <input
            type="text"
            placeholder="Search student, UTR, fee type..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            style={{
              width: '100%',
              padding: '8px 12px 8px 36px',
              borderRadius: '8px',
              border: '1px solid #cbd5e1',
              fontSize: '13px',
              outline: 'none'
            }}
          />
        </div>
      </div>

      {/* Verifications Table */}
      <div style={{ background: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '13px' }}>
          <thead>
            <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', color: '#475569', fontWeight: '700' }}>
              <th style={{ padding: '14px 18px' }}>Student Details</th>
              <th style={{ padding: '14px 18px' }}>Fee Type</th>
              <th style={{ padding: '14px 18px' }}>Amount</th>
              <th style={{ padding: '14px 18px' }}>Payment Mode</th>
              <th style={{ padding: '14px 18px' }}>UTR / Reference No</th>
              <th style={{ padding: '14px 18px' }}>Submitted At</th>
              <th style={{ padding: '14px 18px' }}>Status</th>
              <th style={{ padding: '14px 18px', textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="8" style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
                  <RefreshCw size={24} className="animate-spin" style={{ margin: '0 auto 10px auto' }} />
                  Loading payment verifications...
                </td>
              </tr>
            ) : filteredVerifications.length === 0 ? (
              <tr>
                <td colSpan="8" style={{ padding: '48px 20px', textAlign: 'center', color: '#94a3b8' }}>
                  <Clock size={36} color="#cbd5e1" style={{ margin: '0 auto 12px auto' }} />
                  <div style={{ fontSize: '15px', fontWeight: '600', color: '#475569' }}>No verification requests found</div>
                  <div style={{ fontSize: '12px', marginTop: '4px' }}>
                    {activeFilter === 'PENDING_VERIFICATION' ? 'All parent payments have been processed and verified.' : 'No records match the current filter.'}
                  </div>
                </td>
              </tr>
            ) : (
              filteredVerifications.map(item => (
                <tr key={item.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '14px 18px' }}>
                    <div style={{ fontWeight: '700', color: '#0f172a' }}>{item.studentName || 'Student'}</div>
                    <div style={{ fontSize: '12px', color: '#64748b' }}>
                      {item.studentId} • {item.studentClass || 'Class'} {item.section ? `(${item.section})` : ''}
                    </div>
                  </td>
                  <td style={{ padding: '14px 18px', fontWeight: '600', color: '#334155' }}>
                    {item.feeType}
                  </td>
                  <td style={{ padding: '14px 18px', fontWeight: '800', color: '#0f172a' }}>
                    ₹{Number(item.amount || 0).toLocaleString('en-IN')}
                  </td>
                  <td style={{ padding: '14px 18px' }}>
                    <span style={{ 
                      display: 'inline-flex', 
                      alignItems: 'center', 
                      gap: '4px', 
                      padding: '4px 8px', 
                      borderRadius: '6px', 
                      background: '#f1f5f9', 
                      fontSize: '12px', 
                      fontWeight: '600',
                      color: '#1e293b'
                    }}>
                      <CreditCard size={12} color="#2563eb" />
                      {item.paymentMethod || 'UPI'}
                    </span>
                  </td>
                  <td style={{ padding: '14px 18px' }}>
                    <code style={{ 
                      background: '#f8fafc', 
                      padding: '4px 8px', 
                      borderRadius: '6px', 
                      border: '1px solid #cbd5e1', 
                      fontFamily: 'monospace', 
                      fontWeight: '700',
                      color: '#0f172a',
                      fontSize: '12px'
                    }}>
                      {item.utrNumber}
                    </code>
                  </td>
                  <td style={{ padding: '14px 18px', color: '#64748b', fontSize: '12px' }}>
                    {item.submittedAt ? new Date(item.submittedAt).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '—'}
                  </td>
                  <td style={{ padding: '14px 18px' }}>
                    {item.status === 'PENDING_VERIFICATION' && (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '4px 10px', borderRadius: '12px', background: '#fef3c7', color: '#b45309', fontWeight: '700', fontSize: '11px' }}>
                        <Clock size={12} /> PENDING
                      </span>
                    )}
                    {item.status === 'APPROVED' && (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '4px 10px', borderRadius: '12px', background: '#d1fae5', color: '#047857', fontWeight: '700', fontSize: '11px' }}>
                        <CheckCircle size={12} /> APPROVED
                      </span>
                    )}
                    {item.status === 'REJECTED' && (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '4px 10px', borderRadius: '12px', background: '#fee2e2', color: '#b91c1c', fontWeight: '700', fontSize: '11px' }}>
                        <XCircle size={12} /> REJECTED
                      </span>
                    )}
                  </td>
                  <td style={{ padding: '14px 18px', textAlign: 'right' }}>
                    {item.status === 'PENDING_VERIFICATION' ? (
                      <div style={{ display: 'inline-flex', gap: '8px' }}>
                        <button
                          onClick={() => { setSelectedVerification(item); setShowApproveModal(true); }}
                          style={{
                            padding: '6px 14px',
                            borderRadius: '6px',
                            background: '#059669',
                            color: '#fff',
                            border: 'none',
                            fontWeight: '600',
                            fontSize: '12px',
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}
                        >
                          <CheckCircle size={13} /> Approve
                        </button>
                        <button
                          onClick={() => { setSelectedVerification(item); setShowRejectModal(true); }}
                          style={{
                            padding: '6px 12px',
                            borderRadius: '6px',
                            background: '#fff',
                            color: '#dc2626',
                            border: '1px solid #fca5a5',
                            fontWeight: '600',
                            fontSize: '12px',
                            cursor: 'pointer'
                          }}
                        >
                          Reject
                        </button>
                      </div>
                    ) : item.status === 'APPROVED' ? (
                      <button
                        onClick={() => { setSelectedVerification(item); setShowReceiptModal(true); }}
                        style={{
                          padding: '6px 12px',
                          borderRadius: '6px',
                          background: '#f8fafc',
                          color: '#002366',
                          border: '1px solid #cbd5e1',
                          fontWeight: '600',
                          fontSize: '12px',
                          cursor: 'pointer',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}
                      >
                        <Receipt size={13} /> View Receipt
                      </button>
                    ) : (
                      <span style={{ fontSize: '11px', color: '#94a3b8' }}>
                        {item.rejectionReason || 'Rejected'}
                      </span>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* ═══ APPROVE CONFIRMATION MODAL ═══ */}
      {showApproveModal && selectedVerification && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}>
          <div style={{ background: '#fff', borderRadius: '16px', maxWidth: '480px', width: '100%', padding: '24px', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
              <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: '#d1fae5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CheckCircle size={24} color="#059669" />
              </div>
              <div>
                <h3 style={{ fontSize: '17px', fontWeight: '800', color: '#0f172a', margin: 0 }}>Approve Fee Payment?</h3>
                <p style={{ fontSize: '12px', color: '#64748b', margin: 0 }}>This will generate the official receipt and deduct student fees.</p>
              </div>
            </div>

            {/* Verification Details Box */}
            <div style={{ background: '#f8fafc', borderRadius: '10px', padding: '16px', border: '1px solid #e2e8f0', marginBottom: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ color: '#64748b', fontSize: '13px' }}>Student:</span>
                <span style={{ fontWeight: '700', color: '#0f172a', fontSize: '13px' }}>{selectedVerification.studentName} ({selectedVerification.studentId})</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ color: '#64748b', fontSize: '13px' }}>Fee Type:</span>
                <span style={{ fontWeight: '700', color: '#0f172a', fontSize: '13px' }}>{selectedVerification.feeType}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ color: '#64748b', fontSize: '13px' }}>Amount:</span>
                <span style={{ fontWeight: '800', color: '#059669', fontSize: '16px' }}>₹{Number(selectedVerification.amount).toLocaleString('en-IN')}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#64748b', fontSize: '13px' }}>UTR / Reference:</span>
                <code style={{ fontWeight: '800', color: '#0f172a', fontSize: '13px', background: '#e2e8f0', padding: '2px 6px', borderRadius: '4px' }}>{selectedVerification.utrNumber}</code>
              </div>
            </div>

            {/* Crucial Security Warning */}
            <div style={{ display: 'flex', gap: '10px', background: '#fffbeb', padding: '12px', borderRadius: '8px', border: '1px solid #fde68a', marginBottom: '20px' }}>
              <AlertTriangle size={20} color="#d97706" style={{ flexShrink: 0, marginTop: '2px' }} />
              <div style={{ fontSize: '12px', color: '#92400e', lineHeight: 1.4 }}>
                <strong>Bank Statement Verification:</strong> Ensure that ₹{selectedVerification.amount} with UTR <code>{selectedVerification.utrNumber}</code> has actually credited into the institution's bank account before clicking Approve.
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button
                onClick={() => setShowApproveModal(false)}
                disabled={processing}
                style={{ padding: '8px 16px', borderRadius: '8px', border: '1px solid #cbd5e1', background: '#fff', color: '#475569', fontWeight: '600', cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                onClick={handleApprove}
                disabled={processing}
                style={{
                  padding: '8px 20px',
                  borderRadius: '8px',
                  border: 'none',
                  background: '#059669',
                  color: '#fff',
                  fontWeight: '700',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                <CheckCircle size={15} />
                {processing ? 'Processing...' : 'Approve & Generate Receipt'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ REJECT MODAL ═══ */}
      {showRejectModal && selectedVerification && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}>
          <div style={{ background: '#fff', borderRadius: '16px', maxWidth: '440px', width: '100%', padding: '24px' }}>
            <h3 style={{ fontSize: '17px', fontWeight: '800', color: '#dc2626', margin: '0 0 12px 0' }}>Reject Payment Verification</h3>
            <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '16px' }}>
              Specify the reason for rejecting UTR <strong>{selectedVerification.utrNumber}</strong> for student {selectedVerification.studentName}.
            </p>

            <label style={{ fontSize: '12px', fontWeight: '600', color: '#334155', display: 'block', marginBottom: '6px' }}>Rejection Reason</label>
            <textarea
              value={rejectionReason}
              onChange={e => setRejectionReason(e.target.value)}
              rows={3}
              style={{
                width: '100%',
                padding: '10px',
                borderRadius: '8px',
                border: '1px solid #cbd5e1',
                fontSize: '13px',
                marginBottom: '20px'
              }}
            />

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button
                onClick={() => setShowRejectModal(false)}
                disabled={processing}
                style={{ padding: '8px 16px', borderRadius: '8px', border: '1px solid #cbd5e1', background: '#fff', color: '#475569', fontWeight: '600', cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                onClick={handleReject}
                disabled={processing}
                style={{
                  padding: '8px 20px',
                  borderRadius: '8px',
                  border: 'none',
                  background: '#dc2626',
                  color: '#fff',
                  fontWeight: '700',
                  cursor: 'pointer'
                }}
              >
                {processing ? 'Rejecting...' : 'Confirm Rejection'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ VIEW RECEIPT MODAL ═══ */}
      {showReceiptModal && selectedVerification && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}>
          <div style={{ background: '#fff', borderRadius: '16px', maxWidth: '440px', width: '100%', padding: '24px', textAlign: 'center' }}>
            <div style={{ width: '48px', height: '48px', borderRadius: '50%', background: '#d1fae5', margin: '0 auto 14px auto', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CheckCircle size={28} color="#059669" />
            </div>
            <h3 style={{ fontSize: '18px', fontWeight: '800', color: '#0f172a', margin: '0 0 6px 0' }}>Verified Fee Receipt</h3>
            <p style={{ fontSize: '13px', color: '#64748b', margin: '0 0 16px 0' }}>Receipt generated upon administrator approval</p>

            <div style={{ background: '#f8fafc', borderRadius: '12px', padding: '18px', border: '1px solid #e2e8f0', textAlign: 'left', marginBottom: '20px', fontSize: '13px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ color: '#64748b' }}>Receipt No:</span>
                <span style={{ fontWeight: '800', color: '#002366' }}>{selectedVerification.receiptNo || 'REC-VERIFIED'}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ color: '#64748b' }}>Student:</span>
                <span style={{ fontWeight: '700' }}>{selectedVerification.studentName}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ color: '#64748b' }}>Fee Component:</span>
                <span style={{ fontWeight: '700' }}>{selectedVerification.feeType}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ color: '#64748b' }}>Paid Amount:</span>
                <span style={{ fontWeight: '800', color: '#059669', fontSize: '15px' }}>₹{Number(selectedVerification.amount).toLocaleString('en-IN')}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ color: '#64748b' }}>UTR Reference:</span>
                <code style={{ fontWeight: '700' }}>{selectedVerification.utrNumber}</code>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#64748b' }}>Verified By:</span>
                <span style={{ fontWeight: '600' }}>{selectedVerification.verifiedBy || 'Admin'}</span>
              </div>
            </div>

            <button
              onClick={() => setShowReceiptModal(false)}
              style={{
                width: '100%',
                padding: '10px',
                borderRadius: '8px',
                border: 'none',
                background: '#002366',
                color: '#fff',
                fontWeight: '700',
                cursor: 'pointer'
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
